import { z } from 'zod';
/**
 * Continue with Google or Microsoft (docs/adr/0081-google-and-microsoft-sign-in.md; brief: Identity and access →
 * "Google and Microsoft sign-in are supported as social connections"). Signs in an existing Chapsi account whose
 * address the provider has verified; never creates an account or a workspace. A provider is on only when the server
 * has its client id and secret; otherwise its routes answer 404 and the apps show no button for it.
 */
export const SocialProvider = z.enum(['google', 'microsoft']);
export const SOCIAL_PROVIDER_LABELS = { google: 'Google', microsoft: 'Microsoft' };
/** Which app started: the web app (cookies on the callback), or the desktop or phone app (a handoff code). */
export const SocialClient = z.enum(['web', 'desktop', 'mobile']);
export const SocialProviderParams = z.object({ provider: SocialProvider });
/** The providers this server has set up, in the order the buttons show. Empty: no buttons. */
export const SocialProviders = z.object({ providers: z.array(SocialProvider) });
export const SocialStartInput = z.object({ client: SocialClient.default('web') });
/**
 * Web: `url` is the provider's sign-in page (the browser was bound by an HttpOnly cookie). Desktop and phone: `url` is
 * our one-time launch address for the system browser, and `secret` is kept by the app (keychain, or the page's memory
 * on the desktop) and sent with the handoff code to finish. Shown once; only its hash is stored.
 */
export const SocialStartResponse = z.object({ url: z.string(), secret: z.string().optional() });
/** The provider's answer on our callback, as it sends it. */
export const SocialCallbackQuery = z.object({
    code: z.string().max(4000).optional(),
    state: z.string().max(500).optional(),
    iss: z.string().max(1000).optional(),
    scope: z.string().max(2000).optional(),
    authuser: z.string().max(20).optional(),
    hd: z.string().max(300).optional(),
    prompt: z.string().max(100).optional(),
    session_state: z.string().max(1000).optional(),
    error: z.string().max(200).optional(),
    error_description: z.string().max(2000).optional(),
    error_uri: z.string().max(2000).optional(),
});
export const SocialLaunchParams = z.object({ token: z.string().min(20).max(200) });
/** Which app's fixed return address a failed launch goes back to (never a free-form address). */
export const SocialLaunchQuery = z.object({ app: z.enum(['desktop', 'mobile']).optional() });
/** The app trades the handoff code (from its return address) and its secret for a session. Single use, two minutes. */
export const SocialExchangeInput = z.object({
    code: z.string().min(20).max(200),
    secret: z.string().min(20).max(200),
});
/** Callbacks and the launch address answer 303 See Other; there is no JSON body. */
export const SocialRedirect = z.object({});
/**
 * Where an app's sign-in comes back to: constants, never taken from a request. The address carries only a single-use
 * handoff code that is useless without the app's secret, or one of our reason codes.
 */
export const MOBILE_SOCIAL_REDIRECT = 'chapsi://social-callback';
export const DESKTOP_SOCIAL_REDIRECT = 'chapsi-desktop://social-callback';
/**
 * Why a provider sign-in did not finish, in our words (`/sign-in?social_error=…` on the web, `?error=…` in the apps).
 * `no_account` carries the verified address in the fragment (`#email=…`) so the sign-in screen can offer to create the
 * account with an emailed link.
 */
export const SocialSignInError = z.enum(['failed', 'expired', 'denied', 'unverified', 'no_account', 'sso_required', 'unavailable']);
export const SOCIAL_ERROR_MESSAGES = {
    failed: "That sign-in didn't work. Try again, or use an emailed link.",
    expired: 'That sign-in took too long or was already used. Start again.',
    denied: 'Sign-in was cancelled.',
    unverified: "That account's email address isn't verified, so Chapsi can't use it. Use an emailed link instead.",
    no_account: "No Chapsi account uses this address yet. We'll email you a link to create one.",
    sso_required: 'Your organisation signs in with single sign-on. Continue with single sign-on instead.',
    unavailable: "That sign-in option isn't available right now. Use an emailed link instead.",
};
/** The button label for the providers on offer: "Continue with Google", "… with Google or Microsoft". */
export function socialButtonLabel(providers) {
    return `Continue with ${providers.map((p) => SOCIAL_PROVIDER_LABELS[p]).join(' or ')}`;
}
/** Reads `#email=…` from a return address's fragment; null unless it is one plausible address. */
export function socialEmailFromFragment(hash) {
    let raw = null;
    for (const pair of hash.replace(/^#/, '').split('&')) {
        const at = pair.indexOf('=');
        if (at < 0 || pair.slice(0, at) !== 'email')
            continue;
        try {
            raw = decodeURIComponent(pair.slice(at + 1).replace(/\+/g, ' '));
        }
        catch {
            return null;
        }
        break;
    }
    if (!raw)
        return null;
    const email = raw.trim().toLowerCase();
    return email.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? email : null;
}
//# sourceMappingURL=social.js.map